import _ from 'lodash';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import types from 'frontend/state/actionTypes';
import {
  inputSource,
  getRawCombinations,
  transformInputToData,
  makeGearOptions,
  isGearValidForOptimize,
  getSelectedGear,
} from 'shared/combinations';
import { inputInfo } from 'shared/addonInput';
import SimcInput from 'shared/simcinput';
import { isUnsupportedHealer } from 'shared/character';
import { itemSocketInfo, slotMap } from 'shared/items';
import { parseSoulbindSimcString } from 'shared/shadowlands';

import { toggleReducer } from 'frontend/state/utils';

import gemEnchantReducer, { selectors as gemEnchantSelectors } from './gemEnchant';
import talentsReducer from './talents';
import enhancementsReducer, { selectors as enhancementsSelectors } from './enhancements';
import azeriteEssencesReducer, { selectors as essenceSelectors } from './azeriteEssences';
import covenantsReducer, { selectors as covenantsSelectors } from './covenants';
import downloadReducer from './download';

const textReducer = (state = '', action = {}) => {
  const { type, payload } = action;
  switch (type) {
    // INPUT_OPTIMIZE_TEXT is for user text entry and component loading actions
    case types.INPUT_OPTIMIZE_TEXT:
      return payload.input;
    // SET_OPTIMIZE_TEXT is used for preloading data from a report page, triggers a different set
    // of actions on the Optimize Input component and avoids a full reload
    case types.SET_OPTIMIZE_TEXT:
      return payload;
    case types.LOAD_OPTIMIZE_REPORT:
      return SimcInput.stringify(payload.optimize.data);
    default:
      return state;
  }
};
const preloadDataReducer = (state = [], action = {}) => {
  const { type, payload } = action;
  switch (type) {
    // load from report
    case types.LOAD_OPTIMIZE_REPORT:
      return payload.optimize.data;
    // clear once text is forcibly set
    case types.SET_OPTIMIZE_TEXT:
    case types.INPUT_OPTIMIZE_TEXT:
      return '';
    default:
      return state;
  }
};

const combinationsReducer = (state = null, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.FETCH_COMBINATIONS_SUCCESS:
      return payload;
    default:
      return state;
  }
};

const optimizeSlotMap = {
  finger: 'rings',
  trinket: 'trinkets',
};

const gearReducer = (state = {}, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.INPUT_OPTIMIZE_TEXT:
    case types.FETCH_GEAR_INFO_REQUEST:
      return {};
    case types.FETCH_GEAR_INFO_SUCCESS:
      return payload;
    case types.ADD_OPTIMIZE_ITEM: {
      const { slot, item } = payload;
      return _.mapValues(state, (items, key) => {
        if (key === slot) {
          return [
            ...items,
            {
              ...item,
              customItem: true,
            },
          ];
        }

        return items;
      });
    }
    case types.LOAD_OPTIMIZE_REPORT:
      return payload.optimize.allGear;
    case types.FETCH_OPTIMIZE_CUSTOM_ITEMS_SUCCESS: {
      const { items } = payload;
      const itemsGrouped = _.groupBy(items, item => {
        const slot = slotMap[item.inventoryType];
        return optimizeSlotMap[slot] || slot;
      });

      return _.mapValues(state, (stateItems, slot) => {
        if (itemsGrouped[slot]) {
          return [
            ...stateItems,
            ..._.map(itemsGrouped[slot], item => ({
              ...item,
              customItem: true,
            })),
          ];
        }
        return stateItems;
      });
    }
    default:
      return state;
  }
};

const loadingReducer = (state = false, action = {}) => {
  const { type } = action;
  switch (type) {
    case types.SET_OPTIMIZE_LOADING:
    case types.FETCH_COMBINATIONS_REQUEST:
    case types.FETCH_GEAR_INFO_REQUEST:
      return true;
    case types.FETCH_COMBINATIONS_SUCCESS:
    case types.FETCH_COMBINATIONS_FAILURE:
    case types.FETCH_GEAR_INFO_SUCCESS:
    case types.FETCH_GEAR_INFO_FAILURE:
    case types.RESET_OPTIMIZE:
      return false;
    default:
      return state;
  }
};

const requireLegendariesReducer = () => false;

const selectedReducer = (state = {}, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.INPUT_OPTIMIZE_TEXT: {
      const { gear } = payload;
      return _.mapValues(gear, items =>
        _.map(
          _.filter(
            _.map(items, (it, idx) => ({ ...it, origIdx: idx })),
            item => item.equipped,
          ),
          item => item.origIdx,
        ),
      );
    }
    case types.TOGGLE_COMBINATION_ITEM: {
      const { slot, index } = payload;
      if (state[slot]) {
        return _.mapValues(state, (selectedItems, selectedSlot) => {
          if (selectedSlot === slot) {
            if (_.includes(selectedItems, index)) {
              return _.without(selectedItems, index);
            }
            return [...selectedItems, index];
          }
          return selectedItems;
        });
      }

      return {
        ...state,
        [slot]: [index],
      };
    }
    case types.SELECT_OPTIMIZE_ITEMS: {
      const { items } = payload;
      const newState = { ...state };
      _.each(items, (entry) => {
        const { slot, index } = entry;
        if (newState[slot]) {
          if (!_.includes(newState[slot], index)) {
            newState[slot] = [
              ...newState[slot],
              index,
            ];
          }
        } else {
          newState[slot] = {
            [slot]: [index],
          };
        }
      });
      return newState;
    }
    // ensure an item is selected if an azerite power is clicked
    case types.TOGGLE_OPTIMIZE_AZERITE_POWER: {
      const { slot, index } = payload;
      if (state[slot]) {
        return _.mapValues(state, (selectedItems, selectedSlot) => {
          if (selectedSlot === slot) {
            return [...selectedItems, index];
          }
          return selectedItems;
        });
      }

      return {
        ...state,
        [slot]: [index],
      };
    }
    case types.LOAD_OPTIMIZE_REPORT:
      return payload.optimize.selected;
    default:
      return state;
  }
};

const selectedAzeritePowersReducer = (state = {}, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.INPUT_OPTIMIZE_TEXT: {
      // handle initial powers already on gear
      const { gear } = payload;
      const gearWithOrigIdx = _.mapValues(
        gear,
        items => _.map(items, (it, idx) => ({ ...it, origIdx: idx })),
      );
      const result = _.mapValues(
        gearWithOrigIdx,
        items => _.map(
          _.filter(
            items,
            item => item.azerite_powers,
          ),
          item => ({
            idx: item.origIdx,
            powers: _.map(item.azerite_powers.split('/'), p => parseInt(p, 10)),
          }),
        ),
      );
      return result;
    }
    case types.TOGGLE_OPTIMIZE_AZERITE_POWER: {
      // TODO: can I make this more elegant rather than just copying the entire state every time?
      const { slot, index, power } = payload;
      const newState = { ...state };
      const powers = _.get(_.find(newState[slot], { idx: index }), 'powers');
      let newPowers;
      if (powers) {
        if (_.includes(powers, power)) {
          newPowers = _.without(powers, power);
        } else {
          newPowers = [...powers, power];
        }
      } else {
        if (!newState[slot]) {
          newState[slot] = [];
        }
        if (!_.find(newState[slot], { idx: index })) {
          newState[slot].push({ idx: index, powers: [] });
        }
        newPowers = [power];
      }

      const itemIdx = _.findIndex(newState[slot], { idx: index });
      newState[slot][itemIdx] = {
        idx: index,
        powers: newPowers,
      };

      return newState;
    }
    case types.LOAD_OPTIMIZE_REPORT:
      return payload.optimize.selectedAzeritePowers;
    default:
      return state;
  }
};

// BFA - never use a constraint
const tierReducer = () => () => 0;

const lockReducer = (reducerSlot, loadField) => (state = null, action = {}) => {
  const { type, payload } = action;

  // reset to default on change
  if (type === types.INPUT_OPTIMIZE_TEXT) {
    return null;
  }

  if (!payload) {
    return state;
  }

  const { slot, idx } = payload;
  if (slot !== reducerSlot) {
    return state;
  }
  switch (type) {
    case types.TOGGLE_LOCK:
      // unset on repeat click
      if (state === idx) {
        return null;
      }
      return idx;
    case types.TOGGLE_COMBINATION_ITEM:
      // clear on item toggle
      if (payload.index === state) {
        return null;
      }
      return state;
    case types.LOAD_OPTIMIZE_REPORT:
      return _.get(payload.optimize, loadField);
    default:
      return state;
  }
};

const talentRingOverrideReducer = (state = null, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.TOGGLE_TALENT_RING_OVERRIDE:
      if (state === payload) {
        return null;
      }
      return payload;
    // reset on input change
    case types.INPUT_OPTIMIZE_TEXT:
      return null;
    default:
      return state;
  }
};
const hideLowLevelItemsReducer = toggleReducer({
  toggle: types.TOGGLE_OPTIMIZE_HIDE_LOW_LEVEL,
  initial: true,
  reset: types.RESET_OPTIMIZE,
});
const hideOffspecAzeritePowersReducer = toggleReducer({
  toggle: types.TOGGLE_OPTIMIZE_HIDE_OFFSPEC_AZERITE_POWERS,
  initial: true,
  reset: types.RESET_OPTIMIZE,
});
const enhancementAdvancedControls = toggleReducer({
  toggle: types.TOGGLE_OPTIMIZE_ENHANCEMENT_ADVANCED_CONTROLS,
  initial: false,
  reset: types.RESET_OPTIMIZE,
});
const enhancementReplace = toggleReducer({
  toggle: types.TOGGLE_OPTIMIZE_ENHANCEMENT_REPLACE,
  initial: false,
  reset: types.RESET_OPTIMIZE,
  custom: (state, action) => {
    const { type, payload } = action;
    switch (type) {
      case types.LOAD_OPTIMIZE_REPORT:
        return payload.optimize.enhancementReplace;
      default:
        return state;
    }
  },
});
const essenceAdvancedControls = toggleReducer({
  toggle: types.TOGGLE_OPTIMIZE_ESSENCE_ADVANCED_CONTROLS,
  initial: false,
  reset: types.RESET_OPTIMIZE,
});

export default combineReducers({
  text: textReducer,
  preloadData: preloadDataReducer,
  combinations: combinationsReducer,
  gear: gearReducer,
  selected: selectedReducer,
  selectedAzeritePowers: selectedAzeritePowersReducer,
  requireLegendaries: requireLegendariesReducer,
  tier21Pieces: tierReducer(types.CHOOSE_TIER_21_CONSTRAINT),
  tier20Pieces: tierReducer(types.CHOOSE_TIER_20_CONSTRAINT),
  tier19Pieces: tierReducer(types.CHOOSE_TIER_19_CONSTRAINT),
  loading: loadingReducer,
  gemEnchant: gemEnchantReducer,
  lockedRing: lockReducer('rings', 'lockedRing'),
  lockedTrinket: lockReducer('trinkets', 'lockedTrinket'),
  talentRingOverride: talentRingOverrideReducer,
  hideLowLevelItems: hideLowLevelItemsReducer,
  hideOffspecAzeritePowers: hideOffspecAzeritePowersReducer,
  talents: talentsReducer,
  advancedTalentControls: toggleReducer({
    toggle: types.TOGGLE_OPTIMIZE_ADVANCED_TALENT_CONTROLS,
    initial: false,
    reset: types.RESET_OPTIMIZE,
  }),
  enhancementAdvancedControls,
  enhancementReplace,
  enhancements: enhancementsReducer,

  azeriteEssences: azeriteEssencesReducer,
  essenceAdvancedControls,

  covenants: covenantsReducer,

  download: downloadReducer,

  // BfA corruption placeholders
  corruptionMin: () => null,
  corruptionMax: () => null,
  tryPurification: () => false,
  purifyMode: () => null,
  ignoreLowCorruption: () => false,
  purifyItems: () => [],
});

const base = state => state.simbot.optimize;

const textSelector = state => base(state).text;
const preloadDataSelector = state => base(state).preloadData;
const baseGearSelector = state => base(state).gear;
const gearSelector = createSelector(
  baseGearSelector,
  gear => _.mapValues(gear, items => {
    // save original index
    return _.map(items, (item, index) => {
      return {
        ...item,
        origIdx: index,
        selectedIdx: index,
        socketInfo: item.socketInfo || itemSocketInfo(item),
      };
    });
  }),
);
const equippedGearSelector = createSelector(
  gearSelector,
  gear => _.mapValues(gear, items => _.filter(items, { equipped: true })),
);
const selectedSelector = state => base(state).selected;
const selectedAzeritePowersSelectors = state => base(state).selectedAzeritePowers;
const requireLegendariesSelector = state => base(state).requireLegendaries;
const tier21PiecesSelector = state => base(state).tier21Pieces;
const tier20PiecesSelector = state => base(state).tier20Pieces;
const tier19PiecesSelector = state => base(state).tier19Pieces;
const lockedRingSelector = state => base(state).lockedRing;
const lockedTrinketSelector = state => base(state).lockedTrinket;
const talentRingOverrideSelector = state => base(state).talentRingOverride;
const hideLowLevelItemsSelector = state => base(state).hideLowLevelItems;
const hideOffspecAzeritePowersSelector = state => base(state).hideOffspecAzeritePowers;
const sourceSelector = createSelector(textSelector, inputSource);
const dataSelector = createSelector(textSelector, transformInputToData);
const rawGearSelector = createSelector(dataSelector, makeGearOptions);
const validGearSelector = createSelector(gearSelector, isGearValidForOptimize);
const selectedTalentsSelector = state => base(state).talents.selected;
const lockedTalentsSelector = state => base(state).talents.locked;
const advancedTalentControlsSelector = state => base(state).advancedTalentControls;
const enhancementAdvancedControlsSelector = state => base(state).enhancementAdvancedControls;
const enhancementReplaceSelector = state => base(state).enhancementReplace;
const essenceAdvancedControlsSelector = state => base(state).essenceAdvancedControls;
const equippedTalentsSelector = createSelector(
  dataSelector,
  data => SimcInput.find(data, 'talents'),
);
const equippedEssencesSelector = createSelector(
  dataSelector,
  data => {
    // re-sort to be major/minor/minor/passive/passive to make it easier to compare
    const rawEssences = SimcInput.find(data, 'azerite_essences', true);
    const level = parseInt(SimcInput.find(data, 'level'), 10);
    if (level === 50 && rawEssences) {
      const parts = _.split(rawEssences, '/');
      const sorted = _.join(
        _.sortBy(parts, p => {
          if (_.includes(p, ':')) {
            return -1;
          }
          return 1;
        }),
        '/',
      );
      return sorted;
    }
    return null;
  },
);
const equippedSoulbindSelector = createSelector(
  dataSelector,
  data => {
    const soulbind = SimcInput.find(data, 'soulbind');
    return soulbind;
  },
);
const renownSelector = createSelector(
  dataSelector,
  data => {
    return parseInt(SimcInput.find(data, 'renown'), 10) || null;
  },
);
const unlockedSoulbindTraitsSelector = createSelector(
  dataSelector,
  renownSelector,
  (data, renown) => {
    const soulbinds = _.filter([
      SimcInput.getActiveSoulbind(data),
      ...SimcInput.getInactiveSoulbinds(data),
    ]);
    if (soulbinds.length === 0) {
      return null;
    }
    const traits = new Set();
    _.each(soulbinds, soulbindString => {
      const soulbind = parseSoulbindSimcString(soulbindString, renown);
      if (soulbind) {
        _.each(soulbind.talents, talent => {
          traits.add(_.join([soulbind.id, talent.tier], ':'));
        });
      }
    });
    return [...traits];
  },
);
const selectedGearSelector = createSelector(
  gearSelector,
  selectedSelector,
  selectedAzeritePowersSelectors,
  getSelectedGear,
);
const inputInfoSelector = createSelector(
  textSelector,
  inputInfo,
);
const characterSelector = createSelector(
  dataSelector,
  data => {
    if (data) {
      return SimcInput.toCharacter(data);
    }
    return null;
  },
);
const classIdSelector = createSelector(
  characterSelector,
  char => _.get(char, 'class', null),
);
const specSelector = createSelector(
  dataSelector,
  data => {
    return SimcInput.find(data, 'spec');
  },
);
const azeriteLevelSelector = createSelector(
  characterSelector,
  char => {
    return _.get(char, 'items.neck.azeriteItem.azeriteLevel', 0);
  },
);

// expand selections into full set for all slots, not just the UI data displayed
const selectedEssencesSelector = createSelector(
  essenceSelectors.selected,
  essenceSelectors.lockedMinors,
  azeriteLevelSelector,
  (selectedEssences, lockedMinors, azeriteLevel) => {
    let essences = [...selectedEssences];
    const lockedMinorsLeft = [...lockedMinors];
    // copy minor essences into 2nd minor slot
    if (azeriteLevel >= 65) {
      const slotThree = _.map(_.filter(essences, { slot: 1 }), e => ({ ...e, slot: 2 }));
      essences = [
        ...essences,
        ...slotThree,
      ];
      if (lockedMinorsLeft.length > 0) {
        const toRemove = lockedMinorsLeft.shift();
        // 1st minor slot should only contain the locked minor
        essences = _.reject(essences, e => e.slot === 1 && e.essenceId !== toRemove);
        // 2nd minor slot should not have the locked minor
        essences = _.reject(essences, e => e.slot === 2 && e.essenceId === toRemove);
      }
    }
    // copy 2nd minor essences into 3rd minor slot
    if (azeriteLevel >= 75) {
      const slotFour = _.map(_.filter(essences, { slot: 2 }), e => ({ ...e, slot: 3 }));
      essences = [
        ...essences,
        ...slotFour,
      ];
      // check for second locked minor
      if (lockedMinorsLeft.length > 0) {
        const toRemove = lockedMinorsLeft.shift();
        // 1st minor slot should only contain the locked minor
        essences = _.reject(essences, e => e.slot === 2 && e.essenceId !== toRemove);
        // 2nd minor slot should not have the locked minor
        essences = _.reject(essences, e => e.slot === 3 && e.essenceId === toRemove);
      }
    }
    return essences;
  },
);

const rawCombinationsSelector = createSelector(
  selectedGearSelector,
  gemEnchantSelectors.gem,
  gemEnchantSelectors.cloakEnchant,
  gemEnchantSelectors.ringEnchant,
  gemEnchantSelectors.neckEnchant,
  gemEnchantSelectors.weaponEnchant,
  gemEnchantSelectors.weaponOffhandEnchant,
  gemEnchantSelectors.replaceExisting,
  classIdSelector,
  specSelector,
  selectedTalentsSelector,
  lockedTalentsSelector,
  enhancementsSelectors.selectedEnhancements,
  enhancementReplaceSelector,
  selectedEssencesSelector,
  essenceSelectors.passives,
  enhancementsSelectors.addSocketNum,
  enhancementsSelectors.addSocketGem,
  covenantsSelectors.soulbinds,
  covenantsSelectors.soulbindGroups,
  covenantsSelectors.conduits,
  covenantsSelectors.conduitRankOverride,
  unlockedSoulbindTraitsSelector,
  covenantsSelectors.allSoulbinds,
  renownSelector,
  covenantsSelectors.soulbindsModified,
  getRawCombinations,
);
const loadingSelector = state => state.simbot.optimize.loading;
const dataErrorSelector = createSelector(
  dataSelector,
  data => {
    if (data) {
      const actors = SimcInput.getActors(data);
      if (isUnsupportedHealer(SimcInput.toCharacter(data))) {
        return 'healer';
      }
      if (actors.length === 0) {
        return 'no-actors';
      }
      const invalidCommands = SimcInput.getInvalidCommands(data);
      if (invalidCommands.length > 0) {
        return 'invalid-commands';
      }
    }
    return null;
  },
);

export const selectors = {
  text: textSelector,
  preloadData: preloadDataSelector,
  inputInfo: inputInfoSelector,
  data: dataSelector,
  source: sourceSelector,
  gear: gearSelector,
  selected: selectedSelector,
  selectedAzeritePowers: selectedAzeritePowersSelectors,
  requireLegendaries: requireLegendariesSelector,
  selectedGear: selectedGearSelector,
  rawGear: rawGearSelector,
  validGear: validGearSelector,
  dataError: dataErrorSelector,
  rawCombinations: rawCombinationsSelector,
  tier21Pieces: tier21PiecesSelector,
  tier20Pieces: tier20PiecesSelector,
  tier19Pieces: tier19PiecesSelector,
  lockedRing: lockedRingSelector,
  lockedTrinket: lockedTrinketSelector,
  talentRingOverride: talentRingOverrideSelector,
  loading: loadingSelector,
  hideLowLevelItems: hideLowLevelItemsSelector,
  hideOffspecAzeritePowers: hideOffspecAzeritePowersSelector,
  selectedTalents: selectedTalentsSelector,
  lockedTalents: lockedTalentsSelector,
  advancedTalentControls: advancedTalentControlsSelector,
  enhancementAdvancedControls: enhancementAdvancedControlsSelector,
  enhancementReplace: enhancementReplaceSelector,
  equippedGear: equippedGearSelector,
  equippedTalents: equippedTalentsSelector,
  equippedEssences: equippedEssencesSelector,
  equippedSoulbind: equippedSoulbindSelector,
  selectedEssences: selectedEssencesSelector,
  essenceAdvancedControls: essenceAdvancedControlsSelector,
  addSocketNum: enhancementsSelectors.addSocketNum,
  addSocketGem: enhancementsSelectors.addSocketGem,
  unlockedSoulbindTraits: unlockedSoulbindTraitsSelector,
  classId: classIdSelector,
  spec: specSelector,
  renown: renownSelector,
};
